import { AddPrefixToObject } from "@shared/common/types/AddPrefixToObject";
import { ToastContentProps } from "react-toastify";
import styled, { css } from "styled-components";
import { useCallback } from "react";

export interface MessageToastProps extends Partial<Readonly<ToastContentProps>> {
  readonly message: string;
  readonly type?: "default" | "error";
  readonly actionText?: string;
  readonly onActionClick?: () => void;
}

export function MessageToast({
  type = "default",
  actionText,
  onActionClick,
  message,
  closeToast,
}: MessageToastProps): React.JSX.Element {
  const handleActionClick = useCallback(() => {
    closeToast?.();
    onActionClick?.();
  }, [closeToast, onActionClick]);

  return (
    <Container type={type}>
      {type === "error" && <tractie-icon name="error-filled" title="error" />}
      <span>{message}</span>
      {actionText && (
        <tractie-button
          data-testid="message-toast-action-button"
          level="tertiary"
          condition="inverse"
          onClick={handleActionClick}
        >
          {actionText}
        </tractie-button>
      )}
    </Container>
  );
}

type ContainerProps = AddPrefixToObject<Pick<MessageToastProps, "type">, "$">;

const ContainerStyle = css<ContainerProps>`
  // https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
  &&& {
    max-width: initial;
    min-width: initial;
    color: ${(props) => props.theme.colors.contentInverse};
    flex: 1;
    display: grid;
    grid-template-columns: ${({ $type }) => ($type === "error" ? "24px 1fr auto;" : "1fr auto;")};
    background-color: ${({ theme, $type }) =>
      $type === "error" ? theme.colors.backgroundError : theme.colors.backgroundBrandSecondary};
    border-radius: ${({ theme }) => theme.spacing.spacing3.toString()};
    align-items: center;
    padding: ${({ theme }) =>
      `${theme.spacing.spacing2.toString()} ${theme.spacing.spacing4.toString()}`};
    gap: ${({ theme }) => theme.spacing.spacing2.toString()};
    justify-content: left;
    margin: ${({ theme }) => theme.spacing.spacing2.toString()} 0 0 0;

    span {
      ${({ theme }) => theme.typography.text.regular.base};
      padding: 8px 0;
    }

    /* Shadows/Android/Elevated 2 */
    box-shadow:
      0px 2px 4px 0px rgba(0, 0, 0, 0.16),
      0px 2px 12px 0px rgba(0, 0, 0, 0.24);
  }
`;

function Container({
  type,
  children,
}: Pick<MessageToastProps, "type"> & { readonly children: React.ReactNode }): React.JSX.Element {
  if (type === "error") {
    return <Alert $type={type}>{children}</Alert>;
  }
  return <Status $type={type}>{children}</Status>;
}

const Alert = styled.div<ContainerProps>`
  ${ContainerStyle}
`;

const Status = styled.output<ContainerProps>`
  ${ContainerStyle};
`;
